import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Check, Crown, Gift } from 'lucide-react';
import { track } from '../lib/api';
import { trackPixelEvent } from '../lib/pixels';

const FREE_FEATURES = [
  'Daily forex & trading signals',
  'Market breakdowns in Bangla, Hindi & English',
  'Community of 131,000+ traders',
  'Beginner-friendly roadmap',
  'Risk management rules',
  'Free forever',
];

export default function Pricing({ settings, geo }) {
  const tg = settings?.telegram_link || '#';
  const elite = settings?.elite_apply_link || tg;
  const price = settings?.elite_price || '$1,500';
  const priceNote = settings?.elite_price_note || 'One-time payment. Lifetime access.';
  const eliteCtaLabel = settings?.elite_cta_label || 'Apply for Elite Community';
  const eliteTagline = settings?.elite_tagline || '';
  const eliteFeatures = settings?.elite_features || [];

  function fire(kind) {
    trackPixelEvent('Lead', { source: `pricing_${kind}` });
    track({ type: 'tg_click', country_code: geo?.country_code, country_name: geo?.country_name, source: `pricing_${kind}` });
  }

  return (
    <section id="pricing" className="section-pad bg-[hsl(0_0%_4%)] relative overflow-hidden noise-overlay hero-glow">
      <div className="container-x relative z-10">
        <div className="max-w-3xl mb-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] mb-2">The Choice Is Yours</div>
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">TAKE ACTION.</h2>
          <p className="text-muted-foreground mt-4">Two ways in. Start free with the public community — or, if you’re a serious business owner ready to build multiple income streams, apply directly for the Elite private circle.</p>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {/* FREE PLAN */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl card-surface p-6 sm:p-8"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[rgba(34,199,132,0.12)] text-[hsl(152_78%_55%)] border border-[rgba(34,199,132,0.35)]">
                <Gift className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(152_78%_55%)]">Starter</div>
                <div className="font-semibold text-xl">Free Telegram Community</div>
              </div>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-display uppercase text-6xl">FREE</span>
              <span className="text-sm text-muted-foreground">Forever</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Start learning today. No card, no commitment. Get real signals, education, and community.</p>
            <ul className="mt-6 space-y-2.5">
              {FREE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-3 text-[15px]">
                  <span className="mt-1 shrink-0 h-5 w-5 rounded-full bg-[rgba(34,199,132,0.12)] text-[hsl(152_78%_55%)] flex items-center justify-center border border-[rgba(34,199,132,0.35)]"><Check className="h-3 w-3" /></span>
                  <span className="text-[hsl(0_0%_88%)]">{f}</span>
                </li>
              ))}
            </ul>
            <a href={tg} target="_blank" rel="noreferrer" onClick={() => fire('free')} data-testid="pricing-free-join-button">
              <Button className="mt-7 w-full h-12 font-semibold text-white border-0 cta-sheen">Join Free Telegram</Button>
            </a>
          </motion.div>

          {/* ELITE PLAN */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="relative rounded-3xl p-6 sm:p-8 gold-ring"
            style={{ background: 'linear-gradient(180deg, hsl(240 6% 12%), hsl(240 6% 9%))' }}
          >
            <div className="absolute top-0 inset-x-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(245,185,66,0.85), transparent)' }} />
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold bg-[hsl(43_92%_56%)] text-black">Most Valuable · Elite</div>
            <div className="flex items-center gap-3 mt-4">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[rgba(245,185,66,0.14)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.45)]">
                <Crown className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)]">Private Circle</div>
                <div className="font-semibold text-xl">AlveeTrade Elite</div>
              </div>
            </div>
            {eliteTagline && <p className="text-sm text-[hsl(0_0%_82%)] mt-3 leading-relaxed">{eliteTagline}</p>}
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-display uppercase text-6xl text-[hsl(43_92%_56%)]">{price}</span>
              <span className="text-sm text-muted-foreground">one-time</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{priceNote}</p>
            <ul className="mt-6 space-y-2.5">
              {eliteFeatures.map(f => (
                <li key={f} className="flex items-start gap-3 text-[15px]">
                  <span className="mt-1 shrink-0 h-5 w-5 rounded-full bg-[rgba(245,185,66,0.14)] text-[hsl(43_92%_56%)] flex items-center justify-center border border-[rgba(245,185,66,0.45)]"><Check className="h-3 w-3" /></span>
                  <span className="text-[hsl(0_0%_92%)]">{f}</span>
                </li>
              ))}
            </ul>
            <a href={elite} target="_blank" rel="noreferrer" onClick={() => fire('elite')} data-testid="pricing-elite-apply-button">
              <Button className="mt-7 w-full h-12 font-semibold border-0" style={{ background: 'linear-gradient(180deg, hsl(43 92% 58%), hsl(38 90% 48%))', color: '#1a1100' }}>
                <Crown className="h-4 w-4 mr-2" /> {eliteCtaLabel}
              </Button>
            </a>
            <p className="text-[11px] text-muted-foreground mt-3 text-center">For business owners, serious traders & entrepreneurs only.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
